import { LEGAL_ENABLED, LEGAL_VERSION } from '../legal'
import { supabase } from './supabase'
import { generateCode, hashCode, labelFor } from './activationCode'
import type { TenantType } from '../types'

/**
 * Activation codes: the system administrator issues them, a stranger spends
 * one to open an institution.
 *
 * Issuing goes straight through RLS, activation_codes is readable and
 * writable only by the system administrator (supabase/activation.sql), so no
 * server code is needed to insert a row. Redeeming cannot: it writes to
 * auth.users, so it goes to functions/activate, which is also the only place
 * that may compare a code against the stored hashes.
 *
 * Supabase-only, like services/superadmin.ts and for the same reason: none of
 * this existed before the move, so there is no Firebase counterpart to keep
 * in step with.
 */

export interface ActivationCode {
  id: string
  /** Prefix and first group only, the code itself is not recoverable. */
  label: string
  tenantType: TenantType
  features: Record<string, boolean>
  note: string
  expiresAt: string
  status: 'active' | 'used' | 'revoked'
  usedByTenant: string | null
  usedAt: string | null
  createdAt: string
}

interface Row {
  id: string
  label: string
  tenant_type: string
  features: Record<string, boolean> | null
  note: string
  expires_at: string
  status: string
  used_by_tenant: string | null
  used_at: string | null
  created_at: string
}

const toCode = (r: Row): ActivationCode => ({
  id: r.id,
  label: r.label,
  tenantType: 'restaurant',
  features: r.features ?? {},
  note: r.note,
  expiresAt: r.expires_at,
  status: r.status === 'used' ? 'used' : r.status === 'revoked' ? 'revoked' : 'active',
  usedByTenant: r.used_by_tenant,
  usedAt: r.used_at,
  createdAt: r.created_at,
})

const COLUMNS =
  'id, label, tenant_type, features, note, expires_at, status, used_by_tenant, used_at, created_at'

export async function listCodes(): Promise<ActivationCode[]> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { data, error } = await supabase
    .from('activation_codes')
    .select(COLUMNS)
    .order('created_at', { ascending: false })
    .limit(200)

  if (error) throw new Error(error.message)
  return (data ?? []).map((r) => toCode(r as Row))
}

/**
 * Issues codes and hands them back in plain text, the only moment they
 * exist in readable form. What is stored is the hash, so a code not copied
 * now cannot be recovered later, only replaced.
 */
export async function createCodes(input: {
  count: number
  tenantType: TenantType
  features: Record<string, boolean>
  note: string
  days: number
}): Promise<string[]> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')

  const count = Math.max(1, Math.min(50, Math.floor(input.count)))
  const days = Math.max(1, Math.min(365, Math.floor(input.days)))
  const prefix = 'RST'

  const expiresAt = new Date(Date.now() + days * 86_400_000).toISOString()

  const codes = Array.from({ length: count }, () => generateCode(prefix))
  const rows = await Promise.all(
    codes.map(async (code) => ({
      code_hash: await hashCode(code),
      label: labelFor(code),
      tenant_type: input.tenantType,
      features: input.features,
      note: input.note.trim(),
      expires_at: expiresAt,
    })),
  )

  const { error } = await supabase.from('activation_codes').insert(rows)
  if (error) throw new Error(error.message)

  return codes
}

/**
 * Kills a code that has not been spent.
 *
 * Only 'active' rows: a code that opened an institution is the record of how
 * that institution came to exist, and revoking it afterwards would rewrite
 * that without closing anything.
 */
export async function revokeCode(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { error } = await supabase
    .from('activation_codes')
    .update({ status: 'revoked' })
    .eq('id', id)
    .eq('status', 'active')
  if (error) throw new Error(error.message)
}

/** Permanently removes a spent or revoked row the panel no longer needs. */
export async function deleteCode(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { error } = await supabase.from('activation_codes').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

// ============================================================
// The customer's side, no session, no privileges
// ============================================================

/**
 * Asks whether a code is good, without spending it.
 *
 * The server answers yes or no and never says which kind of no, so a code
 * that once existed cannot be told apart from one that never did.
 */
export async function checkCode(code: string): Promise<boolean> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { data, error } = await supabase.functions.invoke('activate', {
    body: { action: 'check', code },
  })
  if (error) throw new Error('Kod doğrulanamadı. Bağlantınızı kontrol edin.')
  return data?.ok === true
}

/**
 * Kurumu açıp yetkiliye onay postası gönderen yol.
 *
 * Kurum kodu sorulmuyor: addan üretilen adaylar sırasıyla gönderiliyor ve
 * sunucu ilk boştakini seçiyor, boş olup olmadığını kesin bilen tek yer
 * orası. Hesap onaysız açılıyor; postadaki bağlantı onu onaylayıp kişiyi
 * kurumun adresine, oturumu açık halde getiriyor.
 */
export async function openWithConfirmation(input: {
  /** Empty for "Ücretsiz başla": the server opens a 14-day trial instead. */
  code: string
  tenantIds: string[]
  displayName: string
  ownerName: string
  /** `+90` and 10 digits, see utils/phone. */
  phone: string
  email: string
  password: string
  /** Kodsuz kayıtta seçilen paket; kodla açılanda yok. */
  plan?: string
}): Promise<{ tenantId: string; mailSent: boolean }> {
  const data = await invokeRedeem(
    { ...input, origin: window.location.origin, legalVersion: LEGAL_ENABLED ? LEGAL_VERSION : undefined },
    input.code ? 'redeem' : 'signup',
  )
  return { tenantId: String(data.tenantId), mailSent: data.mailSent !== false }
}

/**
 * Onay postasını yeniden gönderir; gelmediyse ya da süresi dolduysa.
 * Supabase aynı adrese dakikada bir postadan fazlasını göndermiyor.
 */
export async function resendConfirmation(email: string, tenantId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo: `${window.location.origin}/${tenantId}` },
  })
  if (error) {
    const text = (error.message ?? '').toLowerCase()
    throw new Error(
      text.includes('rate') || text.includes('seconds')
        ? 'Az önce gönderildi. Bir dakika bekleyip tekrar deneyin.'
        : 'Posta gönderilemedi. Biraz sonra tekrar deneyin.',
    )
  }
}

async function invokeRedeem(
  body: Record<string, unknown>,
  action: 'redeem' | 'signup' = 'redeem',
): Promise<Record<string, unknown>> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')

  const { data, error } = await supabase.functions.invoke('activate', {
    body: { ...body, action },
  })

  if (error) {
    const detail = (await error.context?.json?.().catch(() => null)) as
      | { error?: string }
      | null
    throw new Error(detail?.error ?? 'Kurum açılamadı.')
  }
  if (data?.error) throw new Error(String(data.error))
  return (data ?? {}) as Record<string, unknown>
}

/**
 * Kayıt sonrası: e-posta onaylandı mı (kullanıcı, 11 Eki 2026). Kayıt ekranı
 * birkaç saniyede bir soruyor; onay gelince kendiliğinden giriş yapıyor.
 * Kişi postadaki bağlantıya başka bir cihazda ya da tarayıcıda bassa bile.
 */
export async function isConfirmed(email: string, tenantId: string): Promise<boolean> {
  if (!supabase) return false
  const { data, error } = await supabase.functions.invoke('activate', {
    body: { action: 'confirmed', email, tenantId },
  })
  if (error) return false
  return data?.confirmed === true
}
